import { Clipboard } from "@capacitor/clipboard";
import { IonCol, IonGrid, IonRow, useIonToast } from "@ionic/react";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import styles from "@components/carePlan/CarePlan.module.css";
import CareTeamInvitePopover from "@components/carePlan/CareTeamInvitePopover";
import ClientAvatar from "@components/ClientAvatar";
import Tag from "@components/labels/Tag";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useOpener from "@hooks/useOpener";
import { fiveMinutesInMs } from "@typing/Constants";
import {
  CareTeamInviteCancelDocument,
  CareTeamInviteCancelMutation,
  CareTeamInviteCancelMutationVariables,
  CareTeamInviteResendDocument,
  CareTeamInviteResendMutation,
  CareTeamInviteResendMutationVariables,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { IconList } from "@utils/iconUtils";
import { isMobile } from "@utils/platformUtils";

type Props = {
  careTeamInvite: NonNullable<GetJourneyWithPlanQuery["journey"]["careTeamInvites"]>[number];
};

const CareTeamInvite = ({ careTeamInvite }: Props) => {
  const intl = useIntl();
  const popoverOpener = useOpener();
  const modalOpener = useOpener();
  const [presentToast, dismissToast] = useIonToast();

  const [cancelCareTeamInvite] = useMutationWithValidation<
    CareTeamInviteCancelMutation,
    CareTeamInviteCancelMutationVariables
  >(CareTeamInviteCancelDocument, "careTeamInvite", {
    update: (store, { data }) => {
      store.modify({
        fields: {
          canceled() {
            return true;
          }
        },
        id: store.identify({ ...data?.careTeamInviteCancel?.careTeamInvite })
      });
    }
  });

  const [resendCareTeamInvite] = useMutationWithValidation<
    CareTeamInviteResendMutation,
    CareTeamInviteResendMutationVariables
  >(CareTeamInviteResendDocument, "careTeamInvite");

  const handleCancelCareTeamInvite = () => {
    cancelCareTeamInvite({ id: careTeamInvite.id });
    modalOpener.close();
  };

  const handleCopyInvite = useCallback(() => {
    if (careTeamInvite.link) {
      Clipboard.write({ string: careTeamInvite.link });
      modalOpener.close();
    }
  }, [careTeamInvite.link, modalOpener]);

  const handleResendCareTeamInvite = useCallback(() => {
    if (careTeamInvite.lastSentAt && new Date().getTime() - careTeamInvite.lastSentAt.getTime() > fiveMinutesInMs) {
      resendCareTeamInvite({ id: careTeamInvite.id }).then(response => {
        if (response?.careTeamInviteResend?.careTeamInvite) {
          presentToast({
            buttons: [{ handler: () => dismissToast(), icon: IconList.xmark }],
            color: "primary-500",
            cssClass: "toast",
            duration: 5000,
            message: intl.formatMessage({ id: "pages.client.careProfile.careTeam.resentToast" }),
            position: "top"
          });
        }

        modalOpener.close();
      });
    } else {
      presentToast({
        buttons: [{ handler: () => dismissToast(), icon: IconList.xmark }],
        cssClass: "toastWider",
        duration: 5000,
        message: intl.formatMessage({ id: "pages.client.careProfile.careTeam.resentInvalidToast" }),
        position: "top"
      });
      modalOpener.close();
    }
  }, [
    careTeamInvite.lastSentAt,
    careTeamInvite.id,
    resendCareTeamInvite,
    modalOpener,
    presentToast,
    intl,
    dismissToast
  ]);

  return (
    <div>
      <IonGrid className="margin-20-bottom ion-no-padding" onClick={isMobile() ? modalOpener.open : undefined}>
        <IonRow className="ion-align-items-center">
          <IonCol size="auto">
            <div className="ion-margin-end">
              <ClientAvatar
                bold
                clientId={null}
                color="gray-200"
                firstName={careTeamInvite.email}
                journeyId={null}
                size="large"
              />
            </div>
          </IonCol>
          <IonCol>
            <Heading2 color="gray-300">{careTeamInvite.email}</Heading2>
            <div className="margin-4-top">
              <BodyCopy bold color="gray-300" size="small">
                <FormattedMessage id="pages.profile.pending" />
              </BodyCopy>
            </div>
          </IonCol>
          <IonCol className="ion-hide-lg-down" size="auto">
            <CareTeamInvitePopover careTeamInvite={careTeamInvite} opener={popoverOpener} />
          </IonCol>
        </IonRow>
      </IonGrid>
      <Modal
        headerContent={
          <div className="margin-10-top">
            <Tag color="gray-50" text={intl.formatMessage({ id: "pages.client.careProfile.careTeam.pending" })} />
          </div>
        }
        opener={modalOpener}
        title={careTeamInvite.email}
      >
        {() => (
          <div>
            <ButtonLink
              className={`flex ${styles.careTeamDetail}`}
              onClick={handleResendCareTeamInvite}
              stylized={false}
            >
              <div className="margin-2-top ion-margin-end">
                <Icon color="gray-200" icon="repeat" />
              </div>

              <BodyCopy>
                <FormattedMessage id="pages.profile.resendInvite" />
              </BodyCopy>
            </ButtonLink>
            <ButtonLink className={`flex ${styles.careTeamDetail}`} onClick={handleCopyInvite} stylized={false}>
              <div className="margin-2-top ion-margin-end">
                <Icon color="gray-200" icon="copy" />
              </div>

              <BodyCopy>
                <FormattedMessage id="pages.client.careProfile.careTeam.copy" />
              </BodyCopy>
            </ButtonLink>
            <ButtonLink
              className={`flex ${styles.careTeamDetail}`}
              onClick={handleCancelCareTeamInvite}
              stylized={false}
            >
              <div className="margin-2-top ion-margin-end">
                <Icon color="gray-200" icon="trash" />
              </div>

              <BodyCopy>
                <FormattedMessage id="pages.profile.cancelInvite" />
              </BodyCopy>
            </ButtonLink>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CareTeamInvite;
